import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { SheetCloseContext } from "../../contexts/LogSheetContext";
import "./LogActivitySheet.css";

/**
 * Pin the backdrop to the *visual* viewport rather than the layout one.
 *
 * `position: fixed; inset: 0` measures the layout viewport, which on a phone
 * doesn't shrink when the on-screen keyboard opens — so a bottom-anchored
 * sheet kept its lower half under the keys, and that's where the search box
 * and its results used to live. Sizing the backdrop to `visualViewport`
 * (and offsetting it by however far the browser panned to reveal the focused
 * field) keeps the whole sheet in the part of the screen you can still see.
 * Browsers without the API keep the CSS default.
 */
function useVisualViewportBox(active: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!active || !vv) return;
    const apply = () => {
      const el = ref.current;
      if (!el) return;
      el.style.top = `${vv.offsetTop}px`;
      el.style.height = `${vv.height}px`;
      el.style.bottom = "auto";
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }, [active]);
  return ref;
}

interface LogActivitySheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
}

export function LogActivitySheet({ open, onClose, title, children }: LogActivitySheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const backdropRef = useVisualViewportBox(open);

  if (!open) return null;

  return createPortal(
    <div
      ref={backdropRef}
      className="log-activity-sheet-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="log-activity-sheet"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="log-activity-sheet-grab" aria-hidden="true">
          <span />
        </div>
        <div className="log-activity-sheet-header">
          <h2 className="log-activity-sheet-title">{title ?? "Log Activity"}</h2>
          <button
            type="button"
            className="log-activity-sheet-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="log-activity-sheet-body">
          <SheetCloseContext.Provider value={onClose}>{children}</SheetCloseContext.Provider>
        </div>
      </div>
    </div>,
    document.body
  );
}
